import React, { useState } from "react";
import { useChatContext } from "stream-chat-react";
import { useSelector } from "react-redux";
import Avatar from "../common/Avatar";
import ClubCrest from "../common/ClubCrest";
import ClubCard from "./ClubCard";
import { classifyChannelLane, LANE } from "../../utils/laneMapping";
import { computeInboxModel, channelPerspective } from "../../utils/inboxRoles";
import { countryToFlag } from "../../utils/countryFlag";
import { sharePreviewLine } from "./sharePreview";
import { channelUnreadCount } from "../../utils/chatUnread";
import {
  seedFromChannelData,
  formatRemaining,
  TIMER_PHRASES,
} from "../../utils/chatTimer";

// useIsNarrow — true at the mobile inbox width (≤402px, frames 1s/7d). Drives the
// 48px row avatar (design) vs the 44px desktop avatar. matchMedia is cheap; each
// row subscribes independently.
const useIsNarrow = () => {
  const query = "(max-width: 402px)";
  const read = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;
  const [isNarrow, setIsNarrow] = useState(read);
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setIsNarrow(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return isNarrow;
};

// Turn a role_type slug ("sporting_director") into a display label
// ("Sporting Director"). Leaves already-formatted labels alone.
const humanizeRole = (role) => {
  const raw = String(role || "").trim();
  if (!raw) return "";
  if (/[A-Z]/.test(raw) && raw.includes(" ")) return raw;
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const TimerClockIcon = () => (
  <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M6 3.8V6L7.6 7.1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

// Compact inbox-row timer chip (frame 7a). Reads timer state straight off
// channel.data — no per-row API call — via the shared timer utils. Renders
// nothing when the channel carries no timer fields. States (design 1d/7a):
// green "∞ Permanent" once deactivated, a quiet gray "Expired" chip on expired
// (archive) rows, amber "Nd left" inside the ≤2-day window (#FFF4E5 / #B54708),
// and neutral "Nd" while there's still comfortable runway.
const RowTimerChip = ({ channelData }) => {
  const data = channelData || {};
  const ctx = data._tp_custom_context || {};
  const timerStatus = data.timer_status ?? ctx.timer_status ?? null;
  const rawExpires = data.timer_expires_at ?? ctx.timer_expires_at ?? null;

  // No timer stamped at all → no chip.
  if (rawExpires == null && timerStatus == null) return null;

  const base =
    "inline-flex items-center gap-1 flex-shrink-0 px-2 rounded-full text-[10.5px] font-semibold";

  if (timerStatus === "deactivated" || timerStatus === "permanent") {
    return (
      <span
        className={`${base} bg-[#EAF6EE] text-[#17803D]`}
        style={{ paddingTop: 2, paddingBottom: 2 }}
      >
        ∞ Permanent
      </span>
    );
  }

  const seed = seedFromChannelData(data);
  const info = seed.expiresAtMs != null ? formatRemaining(seed.expiresAtMs) : null;
  const isExpired =
    seed.isExpired || timerStatus === "expired" || info?.expired === true;

  // Expired rows (they live in the per-tab Archive section) read a quiet gray
  // "Expired" chip rather than vanishing (design 1d).
  if (isExpired) {
    return (
      <span
        className={`${base} bg-[#F2F4F7] text-[#98A2B3]`}
        style={{ paddingTop: 2, paddingBottom: 2 }}
      >
        Expired
      </span>
    );
  }

  if (info == null) return null;

  const isDays =
    info.phrase === TIMER_PHRASES.DAYS_LEFT ||
    info.phrase === TIMER_PHRASES.DAY_LEFT;
  const isHours =
    info.phrase === TIMER_PHRASES.HOURS_LEFT ||
    info.phrase === TIMER_PHRASES.HOUR_LEFT;

  // ≤2 days of runway (days ≤ 2, any hours-left window, or the final <1h) turns
  // the chip amber with the explicit "… left" wording (design 7a warning state).
  const inWarningWindow = (isDays && info.count <= 2) || isHours || !isDays;

  let label;
  if (isDays) {
    label = inWarningWindow ? `${info.count}d left` : `${info.count}d`;
  } else if (isHours) {
    label = `${info.count}h left`;
  } else {
    label = "<1h left";
  }

  const palette = inWarningWindow
    ? "text-[#B54708]"
    : "bg-[#F2F4F7] text-[#475467]";

  return (
    <span
      className={`${base} ${palette}`}
      style={{
        paddingTop: 2,
        paddingBottom: 2,
        ...(inWarningWindow ? { background: "#FFF4E5" } : null),
      }}
    >
      <TimerClockIcon />
      {label}
    </span>
  );
};

const CustomChannelPreview = (props) => {
  const {
    channel,
    setActiveChannel,
    activeChannel: currentActiveChannel,
    // T035: stream-chat-react's ChannelPreview keeps a live unread count in
    // React state (debounced, mute-aware, reset on notification.mark_read) and
    // hands it to every custom Preview. Consuming it is what makes the row
    // REPAINT when the count moves; `channel.state.unreadCount` is a plain
    // mutable field a component can never re-render off.
    unread: previewUnread,
    // D24 row actions (frame 5i/5k). variant "main" shows the ⋯ Archive entry
    // (desktop) + swipe-to-archive (mobile); "archive-manual" shows Unarchive;
    // "archive-expired" is read-only (a lock chip, no action). Injected by the
    // owning ChannelList's Preview wrapper.
    variant = "main",
    onArchive,
    onUnarchive,
  } = props;
  const { client } = useChatContext();
  const [clubCardRect, setClubCardRect] = useState(null);
  // D24 desktop ⋯ menu + mobile swipe reveal state.
  const [rowMenuOpen, setRowMenuOpen] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const touchStartXRef = React.useRef(null);
  const isArchiveRow = variant === "archive-manual" || variant === "archive-expired";
  // Viewer capability flags (agent/club) for perspective-relative lane filing.
  const roles = useSelector((state) => state.auth.roles);
  const authUser = useSelector((state) => state.auth.user);
  const caps = React.useMemo(
    () => computeInboxModel({ roles, user: authUser }),
    [roles, authUser],
  );
  // 48px avatar at the mobile inbox width (1s/7d), 44px on desktop (7a). The
  // mobile branch pins initials to the design's 16px (Avatar's default
  // floor(size*0.4) would overshoot to 19px); desktop keeps Avatar's default.
  const isNarrow = useIsNarrow();
  const avatarSize = isNarrow ? 48 : 44;
  const avatarInitialsSize = isNarrow ? 16 : undefined;

  // Get the other member(s) from Stream's state
  const members = Object.values(channel.state.members).filter(
    (member) => member.user?.id !== client.userID
  );
  const otherUserFromStreamState = members[0]?.user; // Stream user object for the other member

  // Find the other user's data from channel.data.users
  let otherUserDataFromChannelData = null;
  if (channel.data?.users && Array.isArray(channel.data.users)) {
    otherUserDataFromChannelData = channel.data.users.find(
      (u) => u.id !== client.userID
    );
  }

  // Contact-person resolution: when the counterpart is a club, prefer the
  // current club admin so the row stays in sync after a handover.
  const clubAdminId = channel.data?.club_admin_account_id;
  if (
    clubAdminId != null &&
    Array.isArray(channel.data?.users) &&
    String(clubAdminId) !== String(client.userID)
  ) {
    const adminUser = channel.data.users.find(
      (u) => String(u.id) === String(clubAdminId)
    );
    if (adminUser) {
      otherUserDataFromChannelData = adminUser;
    }
  }

  // --- Data Extraction ---
  const avatarUrl =
    otherUserDataFromChannelData?.profile_picture_url ||
    otherUserFromStreamState?.image ||
    otherUserDataFromChannelData?.image ||
    "/default_profile.webp";

  const displayName =
    otherUserDataFromChannelData?.name ||
    otherUserDataFromChannelData?.email ||
    otherUserFromStreamState?.name ||
    (channel.data?.name && channel.data.name.startsWith("Pitch:")
      ? channel.data.users?.find((u) => u.id !== client.userID)?.email ||
        otherUserFromStreamState?.id
      : channel.data?.name) ||
    otherUserFromStreamState?.id ||
    "Unknown User";

  const derivedRole =
    otherUserDataFromChannelData?.role || otherUserFromStreamState?.role;
  const derivedRoleLower = derivedRole?.toLowerCase() || "";

  // Club identity stamped on channel data (backend adds club_league/club_logo/
  // club_country/club_wyscout_id alongside club_name/club_id).
  const clubName = otherUserDataFromChannelData?.club_name || null;
  const clubId = otherUserDataFromChannelData?.club_id ?? null;
  // The Wyscout team id — a DIFFERENT id space from club_id (the platform
  // users.Club PK). Only this one may be used to look a club up on the
  // Wyscout-keyed public-profile endpoint / page; club_id resolves to an
  // unrelated club there. Absent for lite/unmatched clubs, and for channels
  // stamped before the backend carried the key: the card then stays sparse.
  const clubWyscoutId = otherUserDataFromChannelData?.club_wyscout_id ?? null;
  const clubLeague = otherUserDataFromChannelData?.club_league || null;
  const clubLogo = otherUserDataFromChannelData?.club_logo || null;
  const clubCountry = otherUserDataFromChannelData?.club_country || null;

  // D19: inline flags where the country is known — club_country always; a
  // person's nationality only when the backend stamped it (agents have no source
  // yet → no person flag, never faked).
  const clubFlag = countryToFlag(clubCountry);
  const personNationality =
    otherUserDataFromChannelData?.nationality ||
    otherUserFromStreamState?.nationality ||
    null;
  const personFlag = countryToFlag(personNationality);

  const isAgent =
    Boolean(otherUserDataFromChannelData?.agency_name) ||
    Boolean(otherUserDataFromChannelData?.agency_id) ||
    derivedRoleLower.includes("agent") ||
    (otherUserDataFromChannelData?.email &&
      otherUserDataFromChannelData.email.toLowerCase().includes("agent"));
  const isClub =
    !isAgent &&
    (Boolean(clubName) ||
      clubId != null ||
      derivedRoleLower.includes("club") ||
      derivedRoleLower.includes("scout") ||
      derivedRoleLower.includes("director") ||
      derivedRoleLower.includes("staff") ||
      (otherUserDataFromChannelData?.email &&
        otherUserDataFromChannelData.email.toLowerCase().includes("club")));

  const lastMessage = channel.state.messages[channel.state.messages.length - 1];

  // Call messages (3e) get the event wording in the row preview, mapped from the
  // last message's call_invitation attachment fields — never the raw "invitation"
  // text. "Missed audio call" / "Audio call · Outgoing · 12 min" / "Audio call".
  const lastCallAttachment = Array.isArray(lastMessage?.attachments)
    ? lastMessage.attachments.find((a) => a?.type === "call_invitation")
    : null;
  const callPreviewText = (() => {
    if (!lastCallAttachment) return null;
    const isVideoCall = lastCallAttachment.call_type === "video";
    const callKind = isVideoCall ? "Video call" : "Audio call";
    const callOutcome = lastCallAttachment.call_outcome || null;
    const seconds = Number(lastCallAttachment.call_duration_seconds);
    const compactDuration =
      Number.isFinite(seconds) && seconds > 0
        ? seconds >= 60
          ? `${Math.floor(seconds / 60)} min`
          : `${seconds} s`
        : null;
    if (callOutcome === "missed")
      return `Missed ${isVideoCall ? "video" : "audio"} call`;
    if (callOutcome === "declined") return `${callKind} declined`;
    if (callOutcome === "no_answer") return "No answer";
    if (callOutcome === "outgoing")
      return compactDuration
        ? `${callKind} · Outgoing · ${compactDuration}`
        : `${callKind} · Outgoing`;
    return callKind; // historic invitation with no outcome field
  })();

  // Share-aware preview: a shared card or a bare platform link renders a human
  // summary ("You shared a player", "Sofia shared a club"), never a raw URL.
  // Reuses the chat renderer's detectors via classifySharePreview. Sits between
  // the call-event line and the raw-text fallback; returns null for plain text
  // and external links, which keep their existing rendering.
  const shareSenderName = lastMessage?.user?.name || displayName || "";
  const shareSenderFirstName =
    String(shareSenderName).trim().split(/\s+/)[0] || "";
  const sharePreviewText = lastMessage
    ? sharePreviewLine({
        message: lastMessage,
        currentUserId: client.userID,
        senderFirstName: shareSenderFirstName,
      })
    : null;

  let lastMessageText = "No messages yet";
  if (callPreviewText) {
    lastMessageText = callPreviewText;
  } else if (sharePreviewText) {
    lastMessageText = sharePreviewText.replace(/\n/g, " ");
  } else if (lastMessage?.text) {
    lastMessageText = lastMessage.text.replace(/\n/g, " ");
  } else if (channel.data?.pitch_id && channel.data?.pitch_details?.player_name) {
    lastMessageText = `Pitch for ${channel.data.pitch_details.player_name}`;
  } else if (channel.data?.name && channel.data.name.startsWith("Pitch:")) {
    lastMessageText = `Discussion about ${channel.data.name}`;
  } else {
    lastMessageText = "Inquiry about a player";
  }

  const lastMessageTime = channel.state.last_message_at
    ? new Date(channel.state.last_message_at).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: false,
      })
    : "";

  const isSelected = channel.id === currentActiveChannel?.id;
  const isOnline = otherUserFromStreamState?.online;
  // T035: the row's unread state comes from the viewer's per-member READ STATE
  // (`channel.state.read[me].unread_messages`) — the same field <Channel> seeds
  // the in-thread unread divider from — combined with the live `unread` prop.
  // It used to read `channel.state.unreadCount`, a derived counter that
  // Channel._countMessageAsUnread() suppresses (muted channel, no "read-events"
  // capability, shadowed/silent messages) and that no React render can observe.
  // See src/utils/chatUnread.js.
  const unreadCount = channelUnreadCount(channel, client.userID, previewUnread);
  const isUnread = unreadCount > 0;

  const handleClick = () => {
    setActiveChannel(channel);
  };

  // D24 archive actions. Archive/unarchive are handled by the owning list (which
  // owns the API call + list refresh); the row only closes its own affordances.
  const doArchive = (e) => {
    e?.stopPropagation?.();
    setRowMenuOpen(false);
    setSwipeX(0);
    onArchive?.(channel);
  };
  const doUnarchive = (e) => {
    e?.stopPropagation?.();
    setRowMenuOpen(false);
    onUnarchive?.(channel);
  };

  // Swipe-to-archive (mobile, frame 5k): drag the row left to reveal a 96px
  // Archive panel; a tap on it archives. Only main-list rows on touch; the
  // desktop path uses the ⋯ menu. Snap open past half, else snap back.
  const swipeEnabled = variant === "main";
  const onTouchStart = (e) => {
    if (!swipeEnabled) return;
    touchStartXRef.current = e.touches[0].clientX;
  };
  const onTouchMove = (e) => {
    if (!swipeEnabled || touchStartXRef.current == null) return;
    const dx = e.touches[0].clientX - touchStartXRef.current;
    setSwipeX(Math.max(-96, Math.min(0, dx)));
  };
  const onTouchEnd = () => {
    if (!swipeEnabled) return;
    touchStartXRef.current = null;
    setSwipeX((x) => (x <= -48 ? -96 : 0));
  };

  const isReadOnly = variant === "archive-expired";

  // ClubCard trigger. In THIS component the trigger lives inside the row's own
  // <button>, whose one job is to open the thread — so the trigger is the small
  // crest badge alone, never the whole club line. It used to be the entire
  // second line (pill + crest + club name + flag): with a club counterpart that
  // line covers the middle of the row, and its stopPropagation() cancelled the
  // row click, so a normal click on the row opened the club popover instead of
  // the chat. That is why an AGENT (whose counterparts are clubs) could not open
  // his own accepted-pitch thread from the list at all, while the CLUB side —
  // whose counterpart is an agent, a line with no handlers — always could
  // (staging, 2026-08-10; the deep link ?channel=… was unaffected because no
  // click is involved). The header's copy of this card keeps the full club line:
  // there the line is not nested inside a primary action.
  const openClubCard = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setClubCardRect(rect);
  };

  // Desktop hover-open (design 5d/6c): a short dwell on the crest opens the
  // card; leaving before the dwell elapses cancels it. Touch devices never
  // fire hover, so this is naturally desktop-only; tap/click still opens it
  // immediately via openClubCard. Once open, the card closes on click-away/Esc
  // exactly like the click path. Bound to the crest for the same reason as the
  // click: a dwell anywhere on the club LINE used to raise the card's
  // full-screen click-away backdrop under a pointer that was merely on its way
  // to the row, and that backdrop then swallowed the click as well.
  const hoverTimerRef = React.useRef(null);
  const openClubCardHover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setClubCardRect(rect), 250);
  };
  const cancelClubCardHover = () => clearTimeout(hoverTimerRef.current);
  React.useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  // --- Person-led row anatomy (frames 7a / 1a) --------------------------------
  // One structure for every counterpart: person avatar (44px, club crest as a
  // quiet mini-badge), name + a quiet second identity (role for clubs, agency for
  // agents), a role pill + context second line, the message preview, and a right
  // column carrying time over the timer chip + unread pill. Club and agent rows
  // differ only in that pill, the crest, and what the second line says.
  const roleLabel = humanizeRole(derivedRole) || (isClub ? "Club" : "Agent");
  const quietText = isClub ? roleLabel : otherUserDataFromChannelData?.agency_name || "";

  // Agent second line: intent · player · origin, from the labels the request /
  // last-minute backend stamps on the channel. Any slot the channel doesn't carry
  // is simply dropped — legacy chats fall back to the bare role pill (anatomy holds).
  const intentLabel =
    channel.data?.tp_intent_label || channel.data?.request_intent_label || null;
  const originLabel = channel.data?.request_origin_label || null;
  const playerRef =
    channel.data?.request_player_label ||
    channel.data?.pitch_details?.player_name ||
    null;

  // Tab discipline (D25): a non-Direct-origin thread that rides the viewer's DM
  // lane carries a "via …" origin pill (e.g. the club-side N2 agent request →
  // DM + "via Agent Request"). Inside the origin's OWN lane the tab conveys the
  // origin, so no pill. inDmTab is resolved from THIS viewer's perspective.
  const perspective = channelPerspective(
    channel.data,
    { agentCapable: caps.agentCapable, clubCapable: caps.clubCapable },
    client.userID,
  );
  const inDmTab = classifyChannelLane(channel, perspective) === LANE.DM;
  // Origin pill carries the "via …" prefix (design 1e/2b: "via Last Minute",
  // "via Agent Requests"); only "Direct" reads without it — and Direct is the
  // absence of an origin, so it never reaches this pill in the first place.
  const originPillText =
    inDmTab && originLabel && !/direct/i.test(originLabel)
      ? /^via\s+/i.test(originLabel)
        ? originLabel
        : `via ${originLabel}`
      : null;

  const agentSecondLine = [
    intentLabel,
    playerRef,
    originPillText ? null : originLabel,
  ]
    .filter(Boolean)
    .join(" · ");

  const timeClass = isUnread
    ? "text-[11px] text-[#1466D8] font-semibold"
    : "text-[11px] text-[#98A2B3]";

  return (
    <>
      <div className="group relative w-[95%] overflow-hidden rounded-[20px]">
        {/* D24 swipe-to-archive panel (mobile, 5k) — revealed behind the row. */}
        {swipeEnabled && (
          <div
            className="absolute inset-y-0 right-0 flex items-center justify-center"
            style={{ width: 96, background: "#475467" }}
          >
            <button
              type="button"
              onClick={doArchive}
              className="flex flex-col items-center gap-1 text-white"
              style={{ fontSize: 11, fontWeight: 600 }}
              aria-label="Archive chat"
            >
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="3" width="12" height="3.5" rx="1" stroke="#fff" strokeWidth="1.5" />
                <rect x="3.2" y="6.5" width="9.6" height="6.5" rx="1" stroke="#fff" strokeWidth="1.5" />
                <path d="M6.5 9.5h3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Archive
            </button>
          </div>
        )}
      <button
        onClick={handleClick}
        disabled={isSelected}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: swipeX ? `translateX(${swipeX}px)` : undefined,
          transition: touchStartXRef.current == null ? "transform .18s" : undefined,
        }}
        className={`relative w-full px-4 py-3 text-left rounded-[20px] cursor-pointer flex items-start gap-3 transition-colors duration-150 ${
          isSelected
            ? "bg-[#F0F6FF] shadow-[inset_2px_0_0_#2283FF]"
            : "bg-white hover:bg-gray-50"
        } ${isReadOnly ? "opacity-75" : ""}`}
      >
        <div className="relative flex-shrink-0">
          <Avatar
            src={avatarUrl}
            alt="Avatar"
            size={avatarSize}
            initialsSize={avatarInitialsSize}
            fallbackText={displayName}
            className="border border-white"
          />
          {isClub && (
            <ClubCrest
              name={clubName || displayName}
              logoUrl={clubLogo}
              variant="badge"
              avatarSize={avatarSize}
            />
          )}
          {isOnline && (
            <div className="absolute bottom-0 left-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          {/* T042-2: the counterpart NAME is the anchor of the row (D18) and
              must survive the squeeze. It used to shrink freely while the quiet
              secondary identity beside it was flex-shrink-0, so a long agency
              name or role label pushed the name down to a single letter
              ("M…") once the timer chip claimed the right column. Now the name
              keeps a floor (min-w) and the quiet text is the element that
              yields and truncates first; overflow-hidden keeps the floor from
              spilling out of the row at the narrowest widths. */}
          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
            <span
              className={`text-[13.5px] ${
                isUnread ? "font-bold" : "font-semibold"
              } text-[#101828] truncate min-w-[92px]`}
              title={displayName}
            >
              {displayName}
            </span>
            {quietText && (
              <span className="text-[11.5px] text-[#98A2B3] truncate min-w-0">
                {quietText}
              </span>
            )}
            {/* D19: person nationality flag (only when stamped). */}
            {personFlag && (
              <span
                className="text-[12px] flex-shrink-0 leading-none"
                aria-label={personNationality ? `Nationality: ${personNationality}` : undefined}
              >
                {personFlag}
              </span>
            )}
          </div>

          {isClub ? (
            <span className="text-[11.5px] text-[#667085] flex items-center gap-1.5 min-w-0 overflow-hidden w-fit max-w-full">
              <span className="px-1.5 py-px rounded-md text-[10px] font-semibold text-[#B4550A] bg-[#FDF1E7] border border-[#F3DDC4] flex-shrink-0">
                Club
              </span>
              {/* The ONE club-card trigger on the row (design 5d/6c "the club
                  crest opens the card"). Everything else on this line is plain
                  text so the row keeps its primary action. */}
              <span
                role="button"
                tabIndex={0}
                aria-label={clubName ? `Club details: ${clubName}` : "Club details"}
                onClick={openClubCard}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openClubCard(e);
                }}
                onMouseEnter={openClubCardHover}
                onMouseLeave={cancelClubCardHover}
                className="flex flex-shrink-0 items-center cursor-pointer"
                data-testid="channel-row-club-crest"
              >
                <ClubCrest name={clubName || displayName} logoUrl={clubLogo} />
              </span>
              <span className="truncate">
                {clubName
                  ? clubLeague
                    ? `${clubName} · ${clubLeague}`
                    : clubName
                  : roleLabel}
              </span>
              {/* D19: club country flag inline (5a). */}
              {clubFlag && (
                <span
                  className="flex-shrink-0 leading-none"
                  aria-label={clubCountry ? `Country: ${clubCountry}` : undefined}
                >
                  {clubFlag}
                </span>
              )}
            </span>
          ) : (
            <div className="text-[11.5px] text-[#667085] flex items-center gap-1.5 min-w-0 overflow-hidden">
              <span className="px-1.5 py-px rounded-md text-[10px] font-semibold text-[#1466D8] bg-[#F0F6FF] border border-[#D6E6FF] flex-shrink-0">
                Agent
              </span>
              {originPillText && (
                <span className="px-1.5 py-px rounded-md text-[10px] font-semibold text-[#667085] bg-[#F8FAFC] border border-[#E6EAF0] flex-shrink-0">
                  {originPillText}
                </span>
              )}
              {agentSecondLine && (
                <span className="truncate">{agentSecondLine}</span>
              )}
            </div>
          )}

          <div
            className={`text-[12.5px] truncate ${
              isUnread
                ? "text-[#344054] font-semibold"
                : "text-[#667085]"
            }`}
            title={lastMessageText}
          >
            {lastMessageText}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span className={timeClass}>{lastMessageTime}</span>
          <div className="flex items-center gap-1.5">
            <RowTimerChip channelData={channel.data} />
            {/* D24: expired (read-only) archive rows carry a lock chip (5i). */}
            {isReadOnly && (
              <span
                className="inline-flex items-center gap-1 flex-shrink-0 px-2 rounded-full text-[10.5px] font-semibold bg-[#F2F4F7] text-[#98A2B3]"
                style={{ paddingTop: 2, paddingBottom: 2 }}
              >
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <rect x="2.5" y="5.5" width="7" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2" />
                  <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" stroke="currentColor" strokeWidth="1.2" />
                </svg>
                read-only
              </span>
            )}
            {/* T035 unread badge (design prompt, screen 1: every thread row
                carries its unread state). Blue count pill, capped at 99+ so a
                three-digit number can't push the timer chip off the row. */}
            {isUnread && (
              <span
                className="flex-shrink-0 bg-[#2283FF] text-white text-[10.5px] font-bold px-1.5 rounded-full min-w-[19px] h-[19px] flex items-center justify-center"
                data-testid="channel-row-unread-badge"
                aria-label={`${unreadCount} unread`}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
          {/* D24: manual-archive rows advertise their auto-return (5i note). */}
          {variant === "archive-manual" && (
            <span className="text-[10px] text-[#98A2B3]">returns on new message</span>
          )}
        </div>
      </button>

      {/* D24 ⋯ row menu (desktop, 5i). Main list → Archive; manual archive →
          Unarchive; expired rows carry no action (read-only). A role="button"
          span (not a nested <button>) avoids invalid button-in-button nesting. */}
      {!isReadOnly && (onArchive || onUnarchive) && (
        <div className="absolute right-3 top-2 hidden md:block">
          <span
            role="button"
            tabIndex={0}
            aria-label="Row actions"
            onClick={(e) => {
              e.stopPropagation();
              setRowMenuOpen((v) => !v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                setRowMenuOpen((v) => !v);
              }
            }}
            className={`flex h-6 w-6 items-center justify-center rounded-md text-[#98A2B3] hover:bg-[#F0F2F6] hover:text-[#475467] ${
              rowMenuOpen ? "bg-[#F0F6FF] text-[#1466D8]" : "opacity-0 group-hover:opacity-100"
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <circle cx="3" cy="8" r="1.4" />
              <circle cx="8" cy="8" r="1.4" />
              <circle cx="13" cy="8" r="1.4" />
            </svg>
          </span>
          {rowMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setRowMenuOpen(false);
                }}
              />
              <div
                className="absolute right-0 z-20 mt-1 w-[200px] rounded-[10px] border border-[#E4E9F0] bg-white py-1 shadow-[0_12px_32px_rgba(16,24,40,.16)]"
                onClick={(e) => e.stopPropagation()}
              >
                {variant === "main" ? (
                  <button
                    type="button"
                    onClick={doArchive}
                    className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-[#F8FAFC]"
                  >
                    <span className="text-[12.5px] font-semibold text-[#344054]">Archive chat</span>
                    <span className="text-[10.5px] text-[#98A2B3]">
                      Only your list — returns on a new message
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={doUnarchive}
                    className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-[#F8FAFC]"
                  >
                    <span className="text-[12.5px] font-semibold text-[#344054]">Unarchive</span>
                    <span className="text-[10.5px] text-[#98A2B3]">Back to your main list</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      </div>

      {clubCardRect && (
        <ClubCard
          club={{
            id: clubId,
            wyscoutId: clubWyscoutId,
            name: clubName,
            club_league: clubLeague,
            club_logo: clubLogo,
            club_country: clubCountry,
          }}
          contactPerson={{ name: displayName, role: humanizeRole(derivedRole) }}
          triggerRect={clubCardRect}
          onClose={() => setClubCardRect(null)}
        />
      )}
    </>
  );
};

export default CustomChannelPreview;
